import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import prosysLogo from '../prosys-logo.png';

const Navbar = ({ user, onLogout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  
  const isActive = (path) => location.pathname === path;
  
  const linkStyle = (path) => ({
    textDecoration: 'none',
    padding: '0.75rem 1.25rem',
    borderRadius: '12px',
    fontSize: '0.95rem',
    fontWeight: '600',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    color: isActive(path) ? '#0f172a' : '#ffffff',
    background: isActive(path) 
      ? 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)' 
      : hoveredLink === path 
        ? 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)' 
        : 'rgba(255,255,255,0.08)',
    border: isActive(path) 
      ? '2px solid rgba(255,255,255,0.4)'
      : '2px solid rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    transform: hoveredLink === path ? 'translateY(-4px) scale(1.08)' : 'translateY(0) scale(1)',
    boxShadow: hoveredLink === path 
      ? '0 20px 40px -10px rgba(0, 0, 0, 0.4), 0 0 40px rgba(59, 130, 246, 0.5), inset 0 1px 2px rgba(255,255,255,0.3)' 
      : isActive(path)
        ? '0 8px 20px rgba(0, 0, 0, 0.2), 0 0 30px rgba(255,255,255,0.15), inset 0 1px 2px rgba(255,255,255,0.2)'
        : '0 2px 8px rgba(0, 0, 0, 0.1)',
    textShadow: hoveredLink === path ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
    filter: hoveredLink === path ? 'brightness(1.2)' : 'brightness(1)',
  });

  const mobileLinkStyle = (path) => ({
    ...linkStyle(path),
    margin: '0.5rem 0',
    textAlign: 'center'
  });

  const buttonStyle = {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    border: '2px solid rgba(255,255,255,0.2)',
    padding: '0.75rem 1.5rem',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '600',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 8px 16px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255,255,255,0.2)',
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <>
      <style>
        {`
          @keyframes pulseGlow {
            0%, 100% {
              box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2), 0 0 30px rgba(255,255,255,0.15), inset 0 1px 2px rgba(255,255,255,0.2);
            }
            50% {
              box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2), 0 0 50px rgba(99, 102, 241, 0.4), inset 0 1px 2px rgba(255,255,255,0.3);
            }
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes shimmer {
            0% {
              transform: translateX(-100%) translateY(-100%) rotate(45deg);
            }
            100% {
              transform: translateX(100%) translateY(100%) rotate(45deg);
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-8px) rotate(180deg);
            }
          }

          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          .nav-link::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            transition: left 0.5s;
          }

          .nav-link:hover::before {
            left: 100%;
          }

          .logout-button::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.3);
            transform: translate(-50%, -50%);
            transition: width 0.6s, height 0.6s;
          }

          .logout-button:hover::before {
            width: 300px;
            height: 300px;
          }

          .logout-button:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
            transform: translateY(-4px) scale(1.08) !important;
            box-shadow: 0 20px 40px rgba(239, 68, 68, 0.5), 0 0 40px rgba(239, 68, 68, 0.4) !important;
            filter: brightness(1.1);
          }

          .logout-button:active {
            transform: translateY(-1px) scale(1.02) !important;
          }

          .menu-button {
            position: relative;
          }

          .menu-button::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.2);
            transform: translate(-50%, -50%);
            transition: width 0.4s, height 0.4s;
          }

          .menu-button:hover::after {
            width: 100px;
            height: 100px;
          }

          .menu-button:hover {
            background: rgba(255,255,255,0.25) !important;
            transform: rotate(180deg) scale(1.2) !important;
            box-shadow: 0 8px 20px rgba(0,0,0,0.3), 0 0 30px rgba(59, 130, 246, 0.5);
          }

          .logo-link:hover img {
            transform: rotate(360deg) scale(1.15);
            filter: drop-shadow(0 0 20px rgba(59, 130, 246, 0.6));
          }

          .logo-link img {
            transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
            filter: drop-shadow(0 4px 8px rgba(0,0,0,0.2));
          }

          .logo-link span {
            transition: all 0.4s ease;
          }

          .logo-link:hover span {
            color: #60a5fa !important;
            text-shadow: 0 0 20px rgba(96, 165, 250, 0.8);
            transform: scale(1.05);
            display: inline-block;
          }

          .username-badge {
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
          }

          .username-badge::before {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent);
            animation: shimmer 3s infinite;
          }

          .username-badge:hover {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(147, 51, 234, 0.3) 100%) !important;
            background-size: 200% 200%;
            animation: gradientShift 3s ease infinite;
            transform: scale(1.1);
            box-shadow: 0 8px 20px rgba(0,0,0,0.3), 0 0 30px rgba(59, 130, 246, 0.4);
            border: 2px solid rgba(59, 130, 246, 0.5);
            border-radius: 12px;
          }

          .navbar-container {
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }

          .mobile-menu-item {
            animation: slideIn 0.4s ease-out backwards;
          }

          .mobile-menu-item:nth-child(1) { animation-delay: 0.05s; }
          .mobile-menu-item:nth-child(2) { animation-delay: 0.1s; }
          .mobile-menu-item:nth-child(3) { animation-delay: 0.15s; }
          .mobile-menu-item:nth-child(4) { animation-delay: 0.2s; }
          .mobile-menu-item:nth-child(5) { animation-delay: 0.25s; }
          .mobile-menu-item:nth-child(6) { animation-delay: 0.3s; }
          .mobile-menu-item:nth-child(7) { animation-delay: 0.35s; }
          .mobile-menu-item:nth-child(8) { animation-delay: 0.4s; }
        `}
      </style>
      <nav className="navbar-container" style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
        backgroundSize: '200% 200%',
        animation: 'gradientShift 10s ease infinite',
        borderBottom: '2px solid rgba(255,255,255,0.15)',
        boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255,255,255,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          {/* Desktop Navigation */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '4.5rem'
          }}>
            {/* Logo */}
            <Link 
              to="/" 
              className="logo-link"
              style={{
                textDecoration: 'none',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.5rem'
              }}
            >
              <img 
                src={prosysLogo} 
                alt="ProSys Logo" 
                style={{
                  height: '2.5rem',
                  width: 'auto',
                  borderRadius: '8px'
                }}
              />
              <span style={{ display: window.innerWidth > 480 ? 'block' : 'none' }}>
                ProSys
              </span>
            </Link>

            {/* Desktop Menu */}
            <div style={{
              display: window.innerWidth > 768 ? 'flex' : 'none',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <Link 
                to="/" 
                className="nav-link"
                style={linkStyle('/')}
                onMouseEnter={() => setHoveredLink('/')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                Dashboard
              </Link>
              <Link 
                to="/todos" 
                className="nav-link"
                style={linkStyle('/todos')}
                onMouseEnter={() => setHoveredLink('/todos')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                Aufgaben
              </Link>
              <Link 
                to="/projects" 
                className="nav-link"
                style={linkStyle('/projects')}
                onMouseEnter={() => setHoveredLink('/projects')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                Projekte
              </Link>
              <Link 
                to="/participants" 
                className="nav-link"
                style={linkStyle('/participants')}
                onMouseEnter={() => setHoveredLink('/participants')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                Teilnehmer
              </Link>
              <Link 
                to="/branches" 
                className="nav-link"
                style={linkStyle('/branches')}
                onMouseEnter={() => setHoveredLink('/branches')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                Standorte
              </Link>
              <Link 
                to="/search" 
                className="nav-link"
                style={linkStyle('/search')}
                onMouseEnter={() => setHoveredLink('/search')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                Suche
              </Link>
              {user?.role === 'admin' && (
                <Link 
                  to="/users" 
                  className="nav-link"
                  style={linkStyle('/users')}
                  onMouseEnter={() => setHoveredLink('/users')}
                  onMouseLeave={() => setHoveredLink(null)}
                >
                  Benutzer
                </Link>
              )}
            </div>

            {/* Desktop User Menu */}
            <div style={{
              display: window.innerWidth > 768 ? 'flex' : 'none',
              alignItems: 'center',
              gap: '1rem',
              marginLeft: '2rem'
            }}>
              <div 
                className="username-badge"
                style={{
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  padding: '0.75rem 1.25rem',
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '12px',
                  fontWeight: '600',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                {user?.username}
              </div>
              <button 
                onClick={onLogout}
                className="logout-button"
                style={buttonStyle}
              >
                Abmelden
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="menu-button"
              style={{
                display: window.innerWidth <= 768 ? 'block' : 'none',
                background: 'rgba(255,255,255,0.1)',
                border: '2px solid rgba(255,255,255,0.2)',
                color: '#ffffff',
                padding: '0.75rem',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '1.5rem',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}
            >
              ☰
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div style={{
              display: window.innerWidth <= 768 ? 'block' : 'none',
              padding: '1.5rem 0',
              borderTop: '2px solid rgba(255,255,255,0.15)'
            }}>
              <Link 
                to="/" 
                className="nav-link mobile-menu-item"
                style={mobileLinkStyle('/')}
                onMouseEnter={() => setHoveredLink('/')}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={() => setMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
              <Link 
                to="/todos" 
                className="nav-link mobile-menu-item"
                style={mobileLinkStyle('/todos')}
                onMouseEnter={() => setHoveredLink('/todos')}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={() => setMobileMenuOpen(false)}
              >
                Aufgaben
              </Link>
              <Link 
                to="/projects" 
                className="nav-link mobile-menu-item"
                style={mobileLinkStyle('/projects')}
                onMouseEnter={() => setHoveredLink('/projects')}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={() => setMobileMenuOpen(false)}
              >
                Projekte
              </Link>
              <Link 
                to="/participants" 
                className="nav-link mobile-menu-item"
                style={mobileLinkStyle('/participants')}
                onMouseEnter={() => setHoveredLink('/participants')}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={() => setMobileMenuOpen(false)}
              >
                Teilnehmer
              </Link>
              <Link 
                to="/branches" 
                className="nav-link mobile-menu-item"
                style={mobileLinkStyle('/branches')}
                onMouseEnter={() => setHoveredLink('/branches')}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={() => setMobileMenuOpen(false)}
              >
                Standorte
              </Link>
              <Link 
                to="/search" 
                className="nav-link mobile-menu-item"
                style={mobileLinkStyle('/search')}
                onMouseEnter={() => setHoveredLink('/search')}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={() => setMobileMenuOpen(false)}
              >
                Suche
              </Link>
              {user?.role === 'admin' && (
                <Link
                  to="/users"
                  className="nav-link mobile-menu-item"
                  style={mobileLinkStyle('/users')}
                  onMouseEnter={() => setHoveredLink('/users')}
                  onMouseLeave={() => setHoveredLink(null)}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Benutzer
                </Link>
              )}
              <div 
                className="username-badge mobile-menu-item"
                style={{
                  color: '#ffffff',
                  textAlign: 'center',
                  padding: '0.75rem',
                  margin: '0.75rem 0',
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '12px',
                  fontWeight: '600'
                }}
              >
                {user?.username}
              </div>
              <button 
                onClick={onLogout}
                className="logout-button mobile-menu-item"
                style={{
                  ...buttonStyle,
                  width: '100%',
                  margin: '0.5rem 0'
                }}
              >
                Abmelden
              </button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;